import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { HardHat, SealCheck, ChalkboardTeacher, IdentificationCard, Warning } from "@phosphor-icons/react";
import { fmtDate } from "../../lib/dateUtil";
import TalkAudioPlayer from "./TalkAudioPlayer";

const QUAL_CLS = { valid: "text-emerald-700", expiring: "text-amber-700 font-semibold", expired: "text-red-700 font-semibold", no_expiry: "text-muted-foreground" };

/** My Work card: RAMS awaiting acknowledgement, toolbox talks to acknowledge, and the operative's own qualifications. */
export default function HsPendingCard() {
  const [p, setP] = useState(null);
  const [quals, setQuals] = useState(null);
  const [showQuals, setShowQuals] = useState(false);

  const load = useCallback(() => {
    API.get("/in-house-portal/hs/pending").then(({ data }) => setP(data)).catch(() => setP({ rams_to_acknowledge: [], toolbox_talks: [], qualification_alerts: [] }));
    API.get("/in-house-portal/my-qualifications").then(({ data }) => setQuals(data)).catch(() => setQuals({ qualifications: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!p || !quals) return null;
  const nothing = !p.rams_to_acknowledge.length && !p.toolbox_talks.length && !p.qualification_alerts.length && !quals.qualifications.length;
  if (nothing) return null;

  const ackTalk = async (t) => {
    try { await API.post(`/in-house-portal/toolbox-talks/${t.id}/acknowledge`); toast.success("Acknowledged"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 border-l-4 border-l-amber-400" data-testid="hs-pending-card">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><HardHat className="h-5 w-5 text-amber-500" weight="fill" /> Health &amp; Safety</h2>
      <div className="space-y-3">
        {p.rams_to_acknowledge.length > 0 && (
          <div data-testid="hs-pending-rams">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">RAMS to read before you arrive</p>
            {p.rams_to_acknowledge.map(r => (
              <Link key={r.id} to={`/my-work/${r.entity_type === "emergency_job" ? "emergency" : "jobs"}/${r.entity_id}`} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded hover:bg-muted" data-testid={`hs-pending-rams-${r.id}`}>
                <SealCheck size={14} className="text-amber-600" /><span className="font-medium flex-1">{r.title}</span><span className="text-muted-foreground">{r.reference}</span>
              </Link>
            ))}
          </div>
        )}
        {p.toolbox_talks.length > 0 && (
          <div data-testid="hs-pending-talks">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Toolbox talks to acknowledge</p>
            {p.toolbox_talks.map(t => (
              <details key={t.id} className="border border-border rounded-md mb-1.5" data-testid={`hs-pending-talk-${t.id}`}>
                <summary className="cursor-pointer text-xs px-3 py-2 flex items-center gap-2"><ChalkboardTeacher size={14} className="text-primary" /><span className="font-medium flex-1">{t.topic}</span><span className="text-muted-foreground">{fmtDate(t.delivered_at)}</span></summary>
                <div className="px-3 pb-3">
                  <TalkAudioPlayer talkId={t.id} audio={t.audio} onRefresh={load} compact testId={`hs-pending-talk-audio-${t.id}`} />
                  <ul className="list-disc pl-4 text-xs space-y-0.5 my-2">{(t.points || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
                  <Button size="sm" variant="outline" onClick={() => ackTalk(t)} data-testid={`hs-pending-talk-ack-${t.id}`}>I've read this talk</Button>
                </div>
              </details>
            ))}
          </div>
        )}
        {p.qualification_alerts.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-2.5 text-xs" data-testid="hs-pending-qual-alerts">
            <p className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1 mb-1"><Warning size={14} /> Qualifications needing renewal</p>
            <ul className="space-y-0.5">{p.qualification_alerts.map((q, i) => <li key={i} className={QUAL_CLS[q.status]}>{q.qualification} — {q.status === "expired" ? "expired" : "expires"} {fmtDate(q.expiry_date)}</li>)}</ul>
          </div>
        )}
        {quals.qualifications.length > 0 && (
          <div data-testid="hs-my-quals">
            <button type="button" onClick={() => setShowQuals(s => !s)} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="hs-my-quals-toggle"><IdentificationCard size={14} /> {showQuals ? "Hide" : "Show"} my qualifications ({quals.qualifications.length})</button>
            {showQuals && (
              <ul className="mt-2 divide-y divide-border border border-border rounded-md text-xs">
                {quals.qualifications.map(q => <li key={q.id} className="flex items-center justify-between gap-2 px-3 py-1.5"><span className="font-medium">{q.qualification}{q.reference_number ? <span className="text-muted-foreground font-normal"> · {q.reference_number}</span> : null}</span><span className={QUAL_CLS[q.status]}>{q.expiry_date ? `${q.status === "expired" ? "Expired" : "Expires"} ${fmtDate(q.expiry_date)}` : "No expiry"}</span></li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
